import React, { useState, KeyboardEvent, useRef } from 'react';
import { Send, AtSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { MentionMenu, MentionOption } from './MentionMenu';

export interface MessageContext {
  mentions?: Array<{ type: 'person' | 'source'; id: string; label: string }>;
}

interface MessageInputProps {
  onSendMessage: (message: string, context?: MessageContext) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  className,
  placeholder = "Type your message...",
}) => {
  const [input, setInput] = useState('');
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [selectedMentions, setSelectedMentions] = useState<Array<{ type: 'person' | 'source'; id: string; label: string }>>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const mentionButtonRef = useRef<HTMLButtonElement>(null);

  const handleSend = () => {
    const trimmed = input.trim();
    if (trimmed && !disabled) {
      const context: MessageContext = {
        mentions: selectedMentions.length > 0 ? selectedMentions : undefined,
      };
      onSendMessage(trimmed, context);
      setInput('');
      setSelectedMentions([]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleMentionSelect = (option: MentionOption) => {
    // Insert mention at the end of the input
    const mentionText = option.type === 'person' ? `@${option.label} ` : `[${option.label}] `;
    setInput(prev => prev + mentionText);

    // Add to selected mentions
    setSelectedMentions(prev => [
      ...prev.filter(m => m.id !== option.id),
      { type: option.type, id: option.id, label: option.label }
    ]);

    // Close menu and focus back on input
    setShowMentionMenu(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newLength = input.length + mentionText.length;
        inputRef.current.setSelectionRange(newLength, newLength);
      }
    }, 0);
  };

  const hasNoBorder = className?.includes("border-0");

  return (
    <div className={cn("bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60", !hasNoBorder && "border-t border-border/50", className)}>
      <div className={cn("flex gap-2 max-w-4xl mx-auto", hasNoBorder ? "p-4" : "p-4")}>
        <Input
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex-1 h-11",
            hasNoBorder && "bg-muted/50 border-border/50 shadow-sm"
          )}
        />

        {/* @ Button with Mention Menu */}
        <MentionMenu
          open={showMentionMenu}
          onOpenChange={setShowMentionMenu}
          onSelect={handleMentionSelect}
          onClose={() => setShowMentionMenu(false)}
        >
          <Button
            ref={mentionButtonRef}
            type="button"
            onClick={() => setShowMentionMenu(true)}
            disabled={disabled}
            variant="outline"
            size="icon"
            className="h-11 w-11"
            aria-label="Add mention or context"
          >
            <AtSign className="h-4 w-4" />
          </Button>
        </MentionMenu>

        <Button
          onClick={handleSend}
          disabled={disabled || !input.trim()}
          size="icon"
          className="h-11 w-11"
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};


